import { Icon, ICONS } from '@/common/components/Icon';
import { DatePickerSelector } from '@/common/components/selectors/DatePickerSelector';
import { formatDueDate } from '@/common/utils/dateFormatters';
import { useAuthStore } from '@/stores/authStore';
import { ActionIcon, Group, Text, Tooltip } from '@mantine/core';
import { checkIsAfterToday, isSameDate } from '@tickitacka/shared';
import { useTranslation } from 'react-i18next';
import { useUpdateTodo } from '../services/useTodoQueries';

export default function DueDateBadge({
    todoId,
    dueDate,
    isButton,
    customButton,
    onDueDateChange,
    withinPortal = true,
}: {
    todoId: string;
    dueDate?: string;
    isButton: boolean;
    customButton?: React.ReactNode;
    withinPortal?: boolean;
    onDueDateChange?: (dueDate: string | null) => void;
}) {
    const { t } = useTranslation('todo');
    const updateTodo = useUpdateTodo();
    const { user } = useAuthStore();

    const isAfterToday: boolean = checkIsAfterToday(dueDate);
    // const [datePickerOpened, setDatePickerOpened] = useState(false);

    const handleDatePickerChange = (date: Date | null) => {
        if (date) {
            try {
                const isCancel = isSameDate(date.toDateString(), dueDate);
                const newDueDate = isCancel ? null : date.toISOString();

                // 상위 컴포넌트로 변경 사항 전달
                if (onDueDateChange) {
                    onDueDateChange(newDueDate);
                }

                updateTodo.mutate({
                    id: todoId,
                    due_date: newDueDate,
                    update_id: user?.id ?? '',
                });
            } catch (e) {
                console.error('Failed to update todo due date', e);
            }
        }
    };

    // dueDate가 null이고 isButton이 false인 경우 아무것도 렌더링하지 않음
    if (!dueDate && !isButton) {
        return null;
    }

    const dueDateItem = (
        <DatePickerSelector
            withinPortal={withinPortal}
            editable={isButton}
            actionIcon={
                customButton ||
                (dueDate ? (
                    <Group
                        gap={4}
                        c={
                            isAfterToday
                                ? 'var(--mantine-color-yellow-8)'
                                : 'var(--mantine-color-red-8)'
                        }
                    >
                        <Icon name={ICONS.COMMON.DUE_DATE} />
                        <Text size="xs" pt={2}>
                            {formatDueDate(dueDate)}
                        </Text>
                    </Group>
                ) : isButton ? (
                    <ActionIcon size="xs" variant="subtle" color="gray">
                        {/* <IconCalendarEvent size="16px" /> */}
                        <Icon name={ICONS.COMMON.DUE_DATE} />
                    </ActionIcon>
                ) : null)
            }
            selectedDate={dueDate ? new Date(dueDate) : new Date()}
            onDateChange={handleDatePickerChange}
            showTodayButton={false}
            showSelectedDate={false}
        />
    );

    return isButton ? (
        <Tooltip
            label={t('component.todoDrawer.due_date_badge.set_due_date')}
            withArrow
        >
            <div>{dueDateItem}</div>
        </Tooltip>
    ) : (
        <>{dueDateItem}</>
    );
}
